import { SidebarProps } from '@acceso/design-system';
import { isUndefined } from 'lodash';
import { useLocation } from 'react-router-dom';

import { useAuthorization } from '@/lib/auth';
import { routerManager } from '@/routes/app-routes';
import { hasRoute } from '@/routes/hasRoute';
import { RouteKey } from '@/routes/RouterManager/types';
import { Menu, MenuItem } from '@/types';

const validatePermissions = (
  isLoading: boolean,
  handler: (permission?: string[]) => boolean | undefined,
  permissions?: string[]
) => isUndefined(permissions) || (!isUndefined(permissions) && !isLoading && handler(permissions));

const createMenuItem = (
  { icon, name, routeName }: MenuItem,
  pathname: string,
  activateClick: boolean
) => ({
  icon,
  name,
  disabled: isUndefined(routeName),
  selected: isUndefined(routeName) ? undefined : hasRoute(pathname, routeName),
  onClick: activateClick
    ? () => {
        routerManager.to(routeName as RouteKey);
      }
    : undefined,
});

const useMenu = ({
  targetMenu,
  resourceType,
}: {
  targetMenu: (MenuItem | Menu)[];
  resourceType: string;
}) => {
  const { pathname } = useLocation();

  const { hasResourcePermission, isLoading } = useAuthorization({
    resourceType,
  });

  const menu: SidebarProps['menu'] = (targetMenu as (MenuItem & Menu)[])
    .filter(({ permissions }) => validatePermissions(isLoading, hasResourcePermission, permissions))
    .map(item => ({
      ...createMenuItem(item, pathname, isUndefined(item.submenu)),
      submenu: item.submenu
        ?.filter(({ permissions }) =>
          validatePermissions(isLoading, hasResourcePermission, permissions)
        )
        .map(item => createMenuItem(item, pathname, !isUndefined(item.routeName))),
    }));

  return menu;
};

export { useMenu };
